import { Currency, useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { CreditGrant, Phase } from 'modules/Cube/domain/cube.domain.types'
import { NEW_CREDIT_GRANT_PATTERN } from 'modules/Cube/domain/cube.constants'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import uniq from 'lodash/fp/uniq'

/**
 * Whether the grant's definition may still be changed.
 *
 * The platform applies a re-sent grant's fields only while it is unfunded, so
 * the boundary is the grant's own funding state, not its schedule's status.
 * `LOADING` and `UNRESOLVED` cover the fetch that resolves it — both withhold
 * editing, but only `LOCKED` means the grant is actually funded.
 */
export type CreditGrantEditing = 'ALLOWED' | 'LOADING' | 'UNRESOLVED' | 'LOCKED'

export type CreditGrantPriceOption = {
  id: string
  label: string
}

export type CreditGrantEditorConnector = {
  phaseId: string
  currency: Currency
  // Present when editing an existing grant (staged or persisted); absent for a
  // new grant.
  existingGrant?: CreditGrant
  // A grant the platform already holds. Its cadences are fixed at creation, so
  // a re-send changing them is refused.
  isPersisted: boolean
  editing: CreditGrantEditing
  prices: CreditGrantPriceOption[]
  onClose: () => void
  // Stage a create/edit into the Cube domain. Nothing is written until the
  // schedule is saved, which carries each phase's full grant spec.
  commit: (grant: CreditGrant) => void
}

export const useCreditGrantEditorConnector =
  (): CreditGrantEditorConnector | null => {
    const navigate = useNavigate()
    const routeParams = useParams<{
      phaseId: Phase['id']
      creditGrantId?: string
    }>()
    const cubeContext = useCubeContext()

    const phase = routeParams.phaseId
      ? cubeContext.queries.resolvedPhases[routeParams.phaseId]
      : undefined

    useEffect(() => {
      // Bail out if we opened the editor for a phase that doesn't exist.
      if (!routeParams.phaseId || !phase) {
        navigate('..')
      }
    }, [routeParams.phaseId, phase, navigate])

    const currency = useMemo(
      () =>
        cubeContext.queries.selectedCurrency ??
        cubeContext.queries.rawData.configuration.currency.default,
      [
        cubeContext.queries.selectedCurrency,
        cubeContext.queries.rawData.configuration.currency.default
      ]
    )

    const existingGrant = useMemo(
      () =>
        routeParams.creditGrantId
          ? phase?.creditGrants.find(
              grant => grant.id === routeParams.creditGrantId
            )
          : undefined,
      [phase, routeParams.creditGrantId]
    )

    const prices = useMemo<CreditGrantPriceOption[]>(
      () =>
        (phase?.prices ?? []).map(price => ({
          id: price.id,
          label: price.name
        })),
      [phase]
    )

    // A staged grant has no server record yet, so nothing needs resolving.
    const persistedGrantId =
      existingGrant && !NEW_CREDIT_GRANT_PATTERN.test(existingGrant.id)
        ? existingGrant.id
        : undefined

    // The phase model the editor reads carries no status, so the funding state
    // comes from the grant endpoint. Shares its cache with the detail page.
    const grantQuery = useQuery(
      dashboardv99990101Client.getCreditGrant,
      { id: persistedGrantId ?? '' },
      { enabled: Boolean(persistedGrantId), staleTime: 1 * MINUTES }
    )

    const editing = useMemo<CreditGrantEditing>(() => {
      if (!persistedGrantId) {
        return 'ALLOWED'
      }
      if (grantQuery.isLoading) {
        return 'LOADING'
      }
      // A grant we could not read is held rather than opened up, but it is not
      // the same as a funded one and must not be reported as such.
      if (!grantQuery.data) {
        return 'UNRESOLVED'
      }
      // Awaiting funding means no ledger account holds the grant yet, which is
      // the window the platform allows its money to move in.
      return grantQuery.data.status === 'AWAITING_FUNDING'
        ? 'ALLOWED'
        : 'LOCKED'
    }, [persistedGrantId, grantQuery.isLoading, grantQuery.data])

    const onClose = useCallback(() => navigate('..'), [navigate])

    const commit = useCallback(
      (grant: CreditGrant) => {
        const existingPhase = routeParams.phaseId
          ? cubeContext.queries.rawData.data.phases[routeParams.phaseId]
          : undefined

        cubeContext.mutators.updateData({
          creditGrants: {
            ...cubeContext.queries.rawData.data.creditGrants,
            [grant.id]: grant
          },
          phases: existingPhase
            ? {
                [existingPhase.id]: {
                  creditGrantIds: uniq([
                    ...existingPhase.creditGrantIds,
                    grant.id
                  ])
                }
              }
            : {}
        })
      },
      [cubeContext, routeParams.phaseId]
    )

    if (!routeParams.phaseId || !phase) {
      return null
    }

    return {
      phaseId: routeParams.phaseId,
      currency,
      existingGrant,
      isPersisted: Boolean(persistedGrantId),
      editing,
      prices,
      onClose,
      commit
    }
  }
